import React from 'react';
import {
  TableIcon,
  ChartPieIcon,
  ChartSquareBarIcon,
} from '@heroicons/react/outline';
import PropTypes from 'prop-types';
import Card from 'partial/components/Card';
import Table from 'partial/components/Table';
import Button from 'partial/components/Button';
import PieChart from 'modules/common/chart/PieChart';
import BarChart from 'modules/common/chart/BarChart';
import DonutChart from 'modules/common/chart/DonutChart';

export const useSchemeCard = (array, default_index = 0) => {
  const [props, setProps] = React.useState(array[default_index]);
  const output = React.useMemo(
    () => ({
      key: props.id,
      ...props,
      actionOptions: array.map((x) => ({
        id: x.id,
        label: x.label,
        onClick: () => {
          setProps(x);
        },
      })),
    }),
    [array, props, setProps]
  );
  return output;
};

// const MAP_DECIMAL_PLACE = {
//   transaction: 0,
//   amount: 2,
// };

function DashboardSchemeCard({
  label,
  hook,
  actionOptions,
  bottomActions,
  tableFormat,
  filter,
  defaultSort,
  defaultChart,
  bar: showBar,
  pie: showPie,
  donut: showDonut,
  table: showTable,
  downloadCsvUrl,
  downloadCsvFilename,
  donutChartOptions,
  barChartOptions,
}) {
  const [chartType, setChartType] = React.useState(defaultChart);
  const [meta, setMeta] = React.useState({ sort: defaultSort });

  const [isLoading, table, chart, subLabel] = hook(filter, meta);

  const tableSource = React.useMemo(
    () => (
      <div className="space-x-1 flex items-center">
        {showPie && (
          <Button
            size="xs"
            primary={chartType === 'pie'}
            icon={ChartPieIcon}
            onClick={() => setChartType('pie')}
          />
        )}
        {showDonut && (
          <Button
            size="xs"
            primary={chartType === 'donut'}
            icon={ChartPieIcon}
            onClick={() => setChartType('donut')}
          />
        )}
        {showBar && (
          <Button
            size="xs"
            primary={chartType === 'bar'}
            icon={ChartSquareBarIcon}
            onClick={() => setChartType('bar')}
          />
        )}
        {showTable && (
          <Button
            size="xs"
            primary={chartType === 'table'}
            icon={TableIcon}
            onClick={() => setChartType('table')}
          />
        )}
      </div>
    ),
    [chartType, setChartType, showBar, showPie, showTable, showDonut]
  );

  return (
    <Card
      label={label}
      actionOptions={actionOptions}
      renderAction={tableSource}
      renderBottomAction={bottomActions}
    >
      <div className="flex flex-col" style={{ height: 300 }}>
        {chartType === 'table' && (
          <Table
            isLoading={isLoading}
            data={table}
            format={tableFormat}
            downloadCsvUrl={downloadCsvUrl}
            downloadFilename={downloadCsvFilename}
            onSort={meta.sort.indexOf('id') > -1 ? false : setMeta}
            sort={meta.sort}
            subLabel={subLabel}
          />
        )}
        {chartType === 'pie' && <PieChart data={chart} isLoading={isLoading} />}
        {chartType === 'donut' && (
          <DonutChart
            data={chart}
            isLoading={isLoading}
            chartOptions={donutChartOptions}
          />
        )}
        {chartType === 'bar' && (
          <BarChart
            data={chart}
            isLoading={isLoading}
            chartOptions={barChartOptions}
          />
        )}
      </div>
    </Card>
  );
}

DashboardSchemeCard.defaultProps = {
  label: 'Data',
  actionOptions: [],
  bottomActions: null,
  filter: {},
  defaultSort: 'id:asc',
  defaultChart: 'table',
  table: true,
  pie: true,
  bar: true,
  donut: false,
  downloadCsvUrl: '',
  downloadCsvFilename: '',
  donutChartOptions: {},
  barChartOptions: {},
};

DashboardSchemeCard.propTypes = {
  bar: PropTypes.bool,
  pie: PropTypes.bool,
  table: PropTypes.bool,
  donut: PropTypes.bool,
  label: PropTypes.string,
  defaultSort: PropTypes.string,
  defaultChart: PropTypes.string,
  downloadCsvUrl: PropTypes.string,
  downloadCsvFilename: PropTypes.string,
  hook: PropTypes.func.isRequired,
  filter: PropTypes.instanceOf(Object),
  actionOptions: PropTypes.instanceOf(Array),
  bottomActions: PropTypes.instanceOf(Object),
  tableFormat: PropTypes.instanceOf(Array).isRequired,
  donutChartOptions: PropTypes.instanceOf(Object),
  barChartOptions: PropTypes.instanceOf(Object),
};

export default DashboardSchemeCard;
